import { USER } from "@/config/api.endpoints"
import type { AxiosInstance } from "axios"

export type OnboardingStep =
  | "CREATE"
  | "CONFIRM"
  | "LINK"
  | "DOWNLOAD"
  | "PAYMENT"
  | "PROFILE"

export interface OnboardingCompleteStatus {
  complete: boolean
}

export interface OnboardingStatus {
  step: OnboardingStatusStep
  completed: boolean
  completedAt: Date
  userData: UserData
}

export interface OnboardingStatusStep {
  stepId: string
  stepCode: OnboardingStep
  stepName: string
  stepDescription: string
  stepSequence: number
  required: boolean
  currentStep: OnboardingStep
  nextStep: OnboardingStep | null
}

export interface UserData {
  id?: string
  firstName?: string
  lastName?: string
  email?: string
  phoneNumber?: string
  termsAccepted?: boolean
  privacyAccepted?: boolean
}

export interface PolicyData {
  privacyAccepted: boolean
  termsAccepted: boolean
}

export const createOnboardingService = (
  client: AxiosInstance,
  baseUrl: string = USER.ONBOARDING,
) => ({
  getOnboardingCompleteStatus: async (): Promise<OnboardingCompleteStatus> => {
    try {
      const { data } = await client.get<OnboardingCompleteStatus>(
        `${baseUrl}/status`,
      )
      return data
    } catch (error) {
      console.error("Failed to fetch onboarding complete status:", error)
      throw error
    }
  },

  getOnboardingStatus: async (): Promise<OnboardingStatus> => {
    try {
      const { data } = await client.get<OnboardingStatus>(`${baseUrl}/progress`)
      return data
    } catch (error) {
      console.error("Failed to fetch onboarding status:", error)
      throw error
    }
  },

  resetStatus: async (stepCode: string): Promise<OnboardingStatus> => {
    try {
      const { data } = await client.put<OnboardingStatus>(
        `${baseUrl}/steps/${stepCode}/reset`,
      )
      return data
    } catch (error) {
      console.error("Failed to reset onboarding status step:", error)
      throw error
    }
  },

  updateOnboardingStep: async (
    stepCode: OnboardingStep,
    info?: string,
  ): Promise<OnboardingStatus> => {
    try {
      const { data } = await client.put<OnboardingStatus>(
        `${baseUrl}/steps/${stepCode}/complete`,
        info,
      )
      return data
    } catch (error) {
      console.error("Failed to update onboarding status step:", error)
      throw error
    }
  },

  acceptPolicy: async (
    stepCode: string,
    policyData: { privacyAccepted: boolean; termsAccepted: boolean },
  ): Promise<OnboardingStatus> => {
    try {
      const { data } = await client.put<OnboardingStatus>(
        `${baseUrl}/steps/${stepCode}/complete}`,
        policyData,
      )
      return data
    } catch (error) {
      console.error("Failed to record policy agreements:", error)
      throw error
    }
  },
})

export type OnboardingService = ReturnType<typeof createOnboardingService>
